import { obtenerSesion, limpiarSesion } from '../storage/authStorage';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL as string;

/** FR-ERR-01: error de la API, con el mensaje devuelto por el backend. */
export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

interface OpcionesPeticion {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  autenticado?: boolean;
}

/**
 * Cliente HTTP centralizado. Adjunta el token de Cognito en rutas autenticadas
 * y mapea los errores del backend a ApiError (FR-ERR-01). Si el backend responde
 * 401 (token vencido/invalido/ausente — lo que devuelve el Cognito Authorizer de
 * API Gateway) o 403 (rol sin permiso) en una ruta autenticada, limpia la sesion
 * local y fuerza la vuelta al login — antes solo se limpiaba el storage sin
 * redirigir, asi que la pagina actual se quedaba mostrando peticiones fallidas.
 */
export async function apiFetch<T>(ruta: string, opciones: OpcionesPeticion = {}): Promise<T> {
  const { method = 'GET', body, autenticado = false } = opciones;

  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (autenticado) {
    const sesion = obtenerSesion();
    if (sesion) headers.Authorization = `Bearer ${sesion.idToken}`;
  }

  const respuesta = await fetch(`${API_BASE_URL}${ruta}`, {
    method,
    headers,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });

  if (respuesta.status === 204) {
    return undefined as T;
  }

  const datos = await respuesta.json().catch(() => ({}));

  if (!respuesta.ok) {
    if ((respuesta.status === 401 || respuesta.status === 403) && autenticado) {
      limpiarSesion();
      if (!window.location.pathname.startsWith('/admin/login')) {
        window.location.assign('/admin/login');
      }
    }
    const mensaje = (datos as { error?: string }).error ?? 'Ocurrió un error, intenta nuevamente.';
    throw new ApiError(respuesta.status, mensaje);
  }

  return datos as T;
}
